import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import { FC, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    cmsImageToOpenGraph,
    fetchCraftData,
    gtm,
    InformationTemplate,
    prefetchContentBlocks,
    PreviewData,
    SponsorsBanner,
    SponsorSet,
} from 'shared';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import InstituteBreadcrumbs from '../../components/InstituteBreadcrumbs';
import {
    SponsorsQuery,
    SponsorsQueryVariables,
    StrandDetailQuery,
    StrandDetailQueryVariables,
    StrandPathsDocument,
    StrandPathsQuery,
    StrandPathsQueryVariables,
    useSponsorsQuery,
    useStrandDetailQuery,
} from '../../gql/cms';
import loadIntlMessages from '../../utils/loadIntlMessages';
import prefetchGlobals from '../../utils/prefetchGlobals';
import siteHandleByLocale, { getLocaleBySiteHandle } from '../../utils/siteHandleByLocale';
import { DEFAULT_REVALIDATION_TIME } from '../[...uri]';

const StrandDetail: FC = () => {
    const { query, asPath, locale, isFallback } = useRouter();
    const vars = useMemo(
        () => ({
            slug: query.slug,
            site: siteHandleByLocale[locale],
        }),
        [query.slug, locale]
    );
    const { data: strandData } = useStrandDetailQuery(vars, { enabled: !isFallback });

    const strand = strandData?.entry.__typename === 'instituteStrands_strand_Entry' && strandData.entry;
    const { title, introText, contentBlocks, sponsorBannerSet, headerImage } = strand;

    useEffect(() => {
        gtm.pageView(asPath, {
            page_pillar: 'institute',
            page_navigation: title,
            page_language: locale,
        });
    }, [asPath, title, locale]);

    return (
        <InformationTemplate
            pageTitle={title}
            pageIntroText={introText}
            pageOpenGraphImages={cmsImageToOpenGraph(headerImage)}
            tableOfContentsTitle={<FormattedMessage defaultMessage="Inhoudsopgave" />}
            contentBlocks={{
                contentBlocks,
            }}
            sponsorBanner={
                <SponsorsBanner
                    mt={[undefined, null, null, 17]}
                    mb={[10, null, null, undefined]}
                    sponsorSet={sponsorBannerSet?.[0] as SponsorSet}
                    maxW="368px"
                />
            }
            breadcrumbs={
                <InstituteBreadcrumbs
                    parentUri="strands"
                    currentPage={{
                        title,
                        uri: asPath,
                    }}
                    onImage
                />
            }
        />
    );
};

export const getStaticPaths: GetStaticPaths = async () => {
    const data = await fetchCraftData<StrandPathsQuery, StrandPathsQueryVariables>({
        query: StrandPathsDocument,
    });
    return {
        paths: data.instituteStrandsEntries.map(entry => ({
            params: {
                slug: entry.slug,
                locale: getLocaleBySiteHandle(entry.siteHandle),
            },
        })),
        fallback: true,
    };
};

export const getStaticProps: GetStaticProps = async ({ params, locale, previewData }) => {
    const queryClient = new QueryClient();
    const variables: StrandDetailQueryVariables = {
        slug: params.slug,
        site: siteHandleByLocale[locale],
    };

    const data = await queryClient.fetchQuery(useStrandDetailQuery.getKey(variables), () =>
        fetchCraftData<StrandDetailQuery, StrandDetailQueryVariables>({
            query: useStrandDetailQuery.document,
            variables,
            previewData: previewData as PreviewData,
        })
    );

    if (data.entry?.__typename !== 'instituteStrands_strand_Entry') {
        return {
            notFound: true,
            revalidate: DEFAULT_REVALIDATION_TIME,
        };
    }

    await queryClient.prefetchQuery(useSponsorsQuery.getKey({}), () =>
        fetchCraftData<SponsorsQuery, SponsorsQueryVariables>({
            query: useSponsorsQuery.document,
            variables: {},
            previewData: previewData as PreviewData,
        })
    );

    await prefetchContentBlocks(queryClient, data.entry.contentBlocks, locale);
    await prefetchGlobals(queryClient, locale, { pathType: 'PARENT_URI', uri: 'strands' });

    return {
        props: {
            intlMessages: await loadIntlMessages(locale),
            dehydratedState: dehydrate(queryClient),
        },
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export default StrandDetail;
